'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'

// Client-side mirror of the server's allowlist (route.ts `MIMES` / `MAX_BYTES`)
// — fast feedback only, the server stays authoritative.
const ALLOWED_MIMES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
])
const MAX_BYTES = 5 * 1024 * 1024

const inputClass =
  'block w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-base text-neutral-950 placeholder:text-neutral-500 ring-4 ring-transparent transition focus:border-neutral-950 focus:ring-neutral-950/5 focus:outline-hidden'

const fileInputClass =
  'block w-full text-sm text-neutral-600 file:mr-4 file:rounded-full file:border-0 file:bg-neutral-950 file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-white file:transition hover:file:bg-neutral-800'

// Label is programmatically tied to the control (htmlFor/id) and errors are
// announced (role=alert) and associated (aria-describedby) — same contract
// as the diagnostic intake / contact forms.
function Field({ id, label, hint, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-neutral-950">
        {label}
      </label>
      {children}
      {hint && !error && <p className="mt-1.5 text-xs text-neutral-500">{hint}</p>}
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-sm text-red-600">
          {error.message}
        </p>
      )}
    </div>
  )
}

function validateFile({ fileList, required, label }) {
  const file = fileList && fileList.length > 0 ? fileList[0] : null
  if (!file) return required ? `Please attach your ${label}.` : true
  if (!ALLOWED_MIMES.has(file.type)) return `${label} must be a PDF or Word document (.pdf, .doc, .docx).`
  if (file.size > MAX_BYTES) return `${label} must be under 5MB.`
  return true
}

export default function ApplyForm({ postingId, postingTitle }) {
  const [serverError, setServerError] = useState(null)
  const [submitted, setSubmitted] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      linkedinUrl: '',
      portfolioUrl: '',
      coverLetter: '',
      consent: false,
    },
  })

  const onSubmit = async (_data, event) => {
    setServerError(null)
    try {
      const formData = new FormData(event.target)
      const res = await fetch('/api/careers/apply', { method: 'POST', body: formData })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || 'Something went wrong — please try again.')
      setSubmitted(true)
    } catch (err) {
      setServerError(err.message || 'Something went wrong — please try again.')
    }
  }

  if (submitted) {
    return (
      <div
        role="status"
        className="rounded-2xl border border-neutral-200 bg-neutral-50 p-8 text-center"
      >
        <p className="font-display text-xl font-semibold text-neutral-950">
          Application received
        </p>
        <p className="mt-2 text-base text-neutral-600">
          Thanks for applying{postingTitle ? ` to the ${postingTitle} role` : ''} — check your
          inbox for a confirmation email. We read every application and will follow up if
          there&apos;s a fit.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <input type="hidden" name="postingId" value={postingId} />

      {/* Honeypot — real visitors never see or fill this field. */}
      <div className="sr-only" aria-hidden="true">
        <label htmlFor="apply-website">Leave this field blank</label>
        <input id="apply-website" type="text" name="website" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="space-y-5">
        <Field id="apply-name" label="Full name" error={errors.name}>
          <input
            id="apply-name"
            type="text"
            autoComplete="name"
            aria-invalid={errors.name ? 'true' : undefined}
            aria-describedby={errors.name ? 'apply-name-error' : undefined}
            className={inputClass}
            {...register('name', { required: 'Please enter your name.' })}
          />
        </Field>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field id="apply-email" label="Email" error={errors.email}>
            <input
              id="apply-email"
              type="email"
              autoComplete="email"
              aria-invalid={errors.email ? 'true' : undefined}
              aria-describedby={errors.email ? 'apply-email-error' : undefined}
              className={inputClass}
              {...register('email', {
                required: 'Please enter your email.',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Enter a valid email address.',
                },
              })}
            />
          </Field>
          <Field id="apply-phone" label="Phone (optional)" error={errors.phone}>
            <input
              id="apply-phone"
              type="tel"
              autoComplete="tel"
              className={inputClass}
              {...register('phone')}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field id="apply-linkedin" label="LinkedIn URL (optional)" error={errors.linkedinUrl}>
            <input
              id="apply-linkedin"
              type="url"
              autoComplete="url"
              placeholder="https://linkedin.com/in/you"
              aria-invalid={errors.linkedinUrl ? 'true' : undefined}
              aria-describedby={errors.linkedinUrl ? 'apply-linkedin-error' : undefined}
              className={inputClass}
              {...register('linkedinUrl', {
                validate: (v) => !v || /^https?:\/\//i.test(v) || 'Include https:// in the URL.',
              })}
            />
          </Field>
          <Field id="apply-portfolio" label="Portfolio URL (optional)" error={errors.portfolioUrl}>
            <input
              id="apply-portfolio"
              type="url"
              autoComplete="url"
              placeholder="https://your-site.com"
              aria-invalid={errors.portfolioUrl ? 'true' : undefined}
              aria-describedby={errors.portfolioUrl ? 'apply-portfolio-error' : undefined}
              className={inputClass}
              {...register('portfolioUrl', {
                validate: (v) => !v || /^https?:\/\//i.test(v) || 'Include https:// in the URL.',
              })}
            />
          </Field>
        </div>

        <Field id="apply-cover-letter" label="Cover letter (optional)" error={errors.coverLetter}>
          <textarea
            id="apply-cover-letter"
            rows={5}
            autoComplete="off"
            placeholder="Tell us why you're a fit for this role."
            className={inputClass}
            {...register('coverLetter')}
          />
        </Field>

        <Field
          id="apply-resume"
          label="Resume"
          hint="PDF or Word document, up to 5MB."
          error={errors.resume}
        >
          <input
            id="apply-resume"
            type="file"
            accept=".pdf,.doc,.docx"
            aria-invalid={errors.resume ? 'true' : undefined}
            aria-describedby={errors.resume ? 'apply-resume-error' : undefined}
            className={fileInputClass}
            {...register('resume', {
              validate: (fileList) => validateFile({ fileList, required: true, label: 'resume' }),
            })}
          />
        </Field>

        <Field
          id="apply-cover-letter-file"
          label="Cover letter file (optional)"
          hint="PDF or Word document, up to 5MB — only if you'd rather attach one than paste text above."
          error={errors.coverLetterFile}
        >
          <input
            id="apply-cover-letter-file"
            type="file"
            accept=".pdf,.doc,.docx"
            aria-invalid={errors.coverLetterFile ? 'true' : undefined}
            aria-describedby={errors.coverLetterFile ? 'apply-cover-letter-file-error' : undefined}
            className={fileInputClass}
            {...register('coverLetterFile', {
              validate: (fileList) =>
                validateFile({ fileList, required: false, label: 'cover letter file' }),
            })}
          />
        </Field>

        <div className="flex items-start gap-3">
          <input
            id="apply-consent"
            type="checkbox"
            value="true"
            aria-invalid={errors.consent ? 'true' : undefined}
            aria-describedby={errors.consent ? 'apply-consent-error' : undefined}
            className="mt-1 h-5 w-5 flex-none rounded border-neutral-300 text-neutral-950 focus:ring-neutral-950/50"
            {...register('consent', {
              required: 'Please confirm you consent before submitting.',
            })}
          />
          <label htmlFor="apply-consent" className="text-sm text-neutral-600">
            I consent to Kingdom Kode storing and reviewing my application materials for this
            role.
          </label>
        </div>
        {errors.consent && (
          <p id="apply-consent-error" role="alert" className="text-sm text-red-600">
            {errors.consent.message}
          </p>
        )}
      </div>

      {serverError && (
        <p role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {serverError}
        </p>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="mt-8 inline-flex w-full items-center justify-center rounded-full bg-neutral-950 px-6 py-4 text-base font-semibold text-white transition hover:bg-neutral-800 disabled:opacity-60 sm:w-auto"
      >
        {isSubmitting ? 'Submitting…' : 'Submit application'}
      </button>
    </form>
  )
}
